import React, { useState } from "react";
import { Bell, Download, FileText, Music2, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";

const SOON = [
  { id: "improvisation", title: "Improvisation, Embellishment & Reharmonisation", desc: "For score readers who want to improve their improvisation. Learn to embellish chords, improve solo playing that requires improvisation, and reharmonise tastefully." },
  { id: "piano-lessons", title: "Direct Piano Lessons", desc: "Piano lessons with Jason: 10 lessons from Beginner to Intermediate, and 10 lessons from Intermediate to Advanced." },
  { id: "musical-director", title: "Choir Directing, Accompanying & Musical Direction", desc: "Choir directing, accompanying a choir or soloist, the difference between playing alone and playing in a band, and being a Musical Director." },
];
const input = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none";

const NotifyForm = ({ course }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault(); setState("busy");
    try { await api.post("/more/interest", { course, email }); setState("done"); toast.success("You're on the list. We'll email you at launch."); }
    catch (err) { setState("idle"); toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Please check your email"); }
  };
  if (state === "done") return <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary" data-testid={`notify-done-${course}`}><Check size={16} /> We'll let you know</p>;
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid={`notify-btn-${course}`}
      className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"><Bell size={15} /> Notify me</button>
  );
  return (
    <form onSubmit={submit} className="mt-5 flex gap-2" data-testid={`notify-form-${course}`}>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className={input} data-testid={`notify-email-${course}`} />
      <button type="submit" disabled={state === "busy"} data-testid={`notify-submit-${course}`} className="rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60">
        {state === "busy" ? <Loader2 size={15} className="animate-spin" /> : "Notify"}
      </button>
    </form>
  );
};

const SoonCourses = () => (
  <section data-testid="coming-soon-section">
    <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Coming soon</p>
    <h2 className="mt-2 font-serif text-3xl sm:text-4xl">New courses on the way</h2>
    <div className="mt-8 grid gap-5 md:grid-cols-3">
      {SOON.map((c, i) => (
        <article key={c.id} className="flex flex-col rounded-3xl border border-border bg-card p-6 transition-transform duration-300 hover:-translate-y-1" style={{ animationDelay: `${i * 80}ms` }} data-testid={`soon-course-${c.id}`}>
          <span className="w-fit rounded-full bg-primary/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary">Coming soon</span>
          <h3 className="mt-4 font-serif text-xl leading-snug">{c.title}</h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
          <NotifyForm course={c.id} />
        </article>
      ))}
    </div>
  </section>
);

const Biography = () => (
  <section className="flex flex-col gap-6 rounded-3xl border border-border bg-card p-8 md:flex-row md:items-center md:justify-between" data-testid="biography-section">
    <div className="flex items-start gap-4">
      <FileText className="mt-1 shrink-0 text-primary" size={28} />
      <div>
        <h2 className="font-serif text-2xl">Jason Thompson's biography</h2>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">The full story: the artists, the stages and the teaching. Download it as a PDF.</p>
      </div>
    </div>
    <a href="/media/jason-thompson-biography.pdf" download="Jason-Thompson-Biography.pdf" data-testid="biography-download"
      className="inline-flex w-fit items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"><Download size={16} /> Download biography</a>
  </section>
);

const FIELDS = [
  ["name", "Your name", "text", true], ["email", "Email", "email", true], ["phone", "Contact number", "tel", true],
  ["event_date", "Day of the event", "date", true], ["event_time", "Time", "time", true],
];

const BandForm = () => {
  const [f, setF] = useState({ website: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setState("busy");
    try { await api.post("/more/band-enquiry", f); setState("done"); }
    catch (err) { setState("idle"); toast.error(err.response?.status === 429 ? "Too many enquiries, please try again later" : "Please check the form (a valid contact number is required)"); }
  };
  if (state === "done") return (
    <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6" data-testid="band-enquiry-done">
      <p className="font-serif text-xl">Thank you, your enquiry has been sent.</p>
      <p className="mt-2 text-sm text-muted-foreground">Jason will call you on the number you gave to talk through your event. We've also emailed you his biography.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="band-enquiry-form">
      {FIELDS.map(([k, label, type, req]) => (
        <label key={k} className="text-sm"><span className="mb-1 block text-muted-foreground">{label}{req && " *"}</span>
          <input type={type} required={req} value={f[k] || ""} onChange={set(k)} className={input} data-testid={`band-${k}`} /></label>
      ))}
      <label className="text-sm sm:col-span-2"><span className="mb-1 block text-muted-foreground">Outline your event and what you need from the band *</span>
        <textarea rows={4} required value={f.details || ""} onChange={set("details")} maxLength={2000} placeholder="e.g. Wedding reception, 3-piece band, two 45-minute sets, gospel and soul" className={input} data-testid="band-details" /></label>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <button type="submit" disabled={state === "busy"} data-testid="band-submit" className="inline-flex w-fit items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
        {state === "busy" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send enquiry
      </button>
    </form>
  );
};

const BookBand = () => (
  <section className="grid gap-8 lg:grid-cols-[1fr_1.3fr]" data-testid="book-band-section" id="book-the-band">
    <div>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Music2 size={14} /> Live events</p>
      <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Book the JT Band</h2>
      <p className="mt-4 text-muted-foreground">Weddings, parties, corporate events and church occasions. Jason and the JT Band bring gospel, soul, funk and jazz to your event. Send your email and contact number with a quick outline of your event, and Jason will call you to talk it through.</p>
      <img src="/media/jt-band.webp" alt="The JT Band performing live" className="mt-6 aspect-[4/3] w-full rounded-3xl object-cover" data-testid="band-photo" />
    </div>
    <div className="rounded-3xl border border-border bg-card p-6 sm:p-8"><BandForm /></div>
  </section>
);

export default function MoreFromJason() {
  return (
    <>
    <Navbar />
    <main className="mx-auto max-w-6xl space-y-20 px-6 py-14" data-testid="more-from-jason-page">
      <header className="max-w-2xl">
        <p className="font-mono text-[11px] uppercase tracking-widest text-primary">More from Jason</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl">Beyond the courses</h1>
        <p className="mt-4 text-base text-muted-foreground">New courses on the way, Jason's biography, and the JT Band for your live events.</p>
      </header>
      <SoonCourses />
      <Biography />
      <BookBand />
    </main>
    </>
  );
}
